import { useTransaction } from '../hooks/useTransaction';
import { useAccount } from '../state/wallet.state';

import { useAtom, useSetAtom } from 'jotai';
import { Network } from 'near-safe';
import { useEffect, useMemo, useRef, useState } from 'react';
import { serializeSignature } from 'viem';
import { EvmTxCardProps } from '../components/chat/transactions/EvmTxCard';
import { createEvmTransactionStatusMessage } from '../lib/transaction-messages';
import {
  TransactionState,
  TransactionStatus,
  activeTransactionIdsAtom,
  createTxId,
  initTransaction,
  transactionAtomFamily,
} from '../store/evmTransactionStore';
import {
  checkTransactionStatus,
  ensureValidHash,
  getRpcUrlForChain,
} from '../utils/evm';

// Ensure evmData is required for the useEVMTransaction hook
export type UseEVMTransactionParams = {
  evmData: NonNullable<EvmTxCardProps['evmData']> | undefined;
  append: EvmTxCardProps['append'];
  messageId: EvmTxCardProps['messageId'];
};

export const useEVMTransaction = ({
  evmData,
  append,
  messageId,
}: UseEVMTransactionParams) => {
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { evmAddress, evmWallet, chainId } = useAccount();
  const initialWalletHash = useRef<string | undefined>(evmWallet?.hash);
  const setActiveTransactionIds = useSetAtom(activeTransactionIdsAtom);
  const messageAppended = useRef(false);

  if (!evmData) {
    return {
      isLoading: false,
      errorMsg: '',
      setErrorMsg: () => {},
      transaction: null,
      handleSmartAction: async () => {},
      network: null,
      evmAddress: '',
      setTransaction: () => {},
    };
  }

  // Create a unique transaction ID for this card
  const txId = useMemo(() => {
    return createTxId(evmData);
  }, [evmData]);

  // Create and use the transaction atom for this specific transaction
  const [transaction, setTransaction] = useAtom(transactionAtomFamily(txId));

  useEffect(() => {
    return () => {
      if (txId) {
        setActiveTransactionIds((prev: string[]) =>
          prev.filter((id: string) => id !== txId),
        );
      }
    };
  }, [txId, setActiveTransactionIds]);

  useEffect(() => {
    if (chainId !== evmData.chainId && evmWallet?.switchChain) {
      evmWallet.switchChain({ chainId: evmData.chainId });
    }
  }, [chainId, evmData.chainId, evmWallet?.switchChain]);

  const [localSignature, setLocalSignature] = useState<string | undefined>(
    undefined,
  );

  const memoizedSignature = useMemo(() => {
    return evmWallet?.signature
      ? serializeSignature(evmWallet.signature)
      : undefined;
  }, [evmWallet?.signature]);

  useEffect(() => {
    const handleAppendSignature = async () => {
      if (memoizedSignature !== localSignature) {
        if (memoizedSignature) {
          setLocalSignature(memoizedSignature);

          await append({
            role: 'system',
            content: JSON.stringify({
              text: 'user signed message',
              signature: memoizedSignature,
            }),
          });

          if (evmWallet) {
            evmWallet.signature = undefined;
          }
        } else {
          setLocalSignature(undefined);
        }
      }
    };

    handleAppendSignature();
  }, [memoizedSignature]);

  const network = Network.fromChainId(evmData.chainId);
  const { handleTxn } = useTransaction({ evmWallet: evmWallet });

  const handleSmartAction = async () => {
    // Initialize transaction state
    if (!txId) return;

    setIsLoading(true);

    // Store initial wallet hash
    initialWalletHash.current = evmWallet?.hash;

    // Initialize transaction in store
    const { initialState } = initTransaction(txId, messageId);
    setTransaction(initialState);

    // Add to active transactions list
    setActiveTransactionIds((prev: string[]) => [...prev, txId]);

    try {
      const adaptedEvmData = {
        ...evmData,
        metaTransactions: [],
      };

      // Execute transaction
      const result = await handleTxn({ evmData: adaptedEvmData });

      // Check if we have transaction hashes
      const evmHashes = (result as any).evmHashes;

      if (evmHashes && Array.isArray(evmHashes) && evmHashes.length > 0) {
        const newTxHash = evmHashes[0];

        // If it's a placeholder hash (starts with 'pending-'), treat it as pending
        const isPending =
          typeof newTxHash === 'string' && newTxHash.startsWith('pending-');

        if (!isPending) {
          // Check if the transaction was successful
          const { success, errorMessage } = await checkTransactionStatus(
            newTxHash,
            evmData.chainId,
          );
          const txStatus = success ? 'success' : 'fail';

          // Update transaction state with real hash
          setTransaction((prev: TransactionState | null) => ({
            ...(prev || initialState),
            txHash: newTxHash,
            status: success ? 'success' : ('error' as TransactionStatus),
            error: errorMessage,
          }));

          if (!messageAppended.current) {
            messageAppended.current = true;
            await append(
              createEvmTransactionStatusMessage(
                evmData,
                txStatus,
                newTxHash,
                messageId,
                errorMessage,
              ),
            );
          }
        } else {
          // For placeholder hash, just keep loading state active

          // Set a temporary hash that will be updated by polling
          setTransaction((prev: TransactionState | null) => ({
            ...(prev || initialState),
            txHash: undefined, // Don't set placeholder hash to UI
            status: 'pending' as TransactionStatus,
          }));

          // Continue in loading state for polling to work
          // Don't append message yet
        }
      }
      // Fallback to wallet hash if result doesn't contain hash
      else if (
        evmWallet?.hash &&
        evmWallet.hash !== initialWalletHash.current
      ) {
        const newTxHash = ensureValidHash(evmWallet.hash);
        // Check if the transaction was successful
        const { success, errorMessage } = await checkTransactionStatus(
          newTxHash,
          evmData.chainId,
        );
        const txStatus = success ? 'success' : 'fail';

        setTransaction((prev: TransactionState | null) => ({
          ...(prev || initialState),
          txHash: newTxHash,
          status: success ? 'success' : ('error' as TransactionStatus),
          error: errorMessage,
        }));

        if (!messageAppended.current) {
          messageAppended.current = true;
          await append(
            createEvmTransactionStatusMessage(
              evmData,
              txStatus,
              newTxHash,
              messageId,
              errorMessage,
            ),
          );
        }
      }

      // Note: don't setIsLoading(false) here if we need to keep polling
      if (
        transaction?.txHash ||
        (evmHashes &&
          evmHashes.length > 0 &&
          !evmHashes[0].startsWith('pending-'))
      ) {
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Unknown error: ${JSON.stringify(error)}`;

      console.error(`Transaction ${txId} error:`, errorMessage);

      // Update transaction state with error
      setTransaction((prev: TransactionState | null) => ({
        ...(prev || initialState),
        status: 'error' as TransactionStatus,
        error: errorMessage,
      }));

      setIsLoading(false);
      setErrorMsg(errorMessage);
      if (!messageAppended.current) {
        messageAppended.current = true;
        await append(
          createEvmTransactionStatusMessage(
            evmData,
            'fail',
            undefined,
            messageId,
            errorMessage,
          ),
        );
      }
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    let retryCount = 0;
    const MAX_RETRIES = 30; // 15 seconds total (30 * 500ms)

    if (
      isLoading &&
      (!transaction?.txHash || transaction?.status === 'pending')
    ) {
      interval = setInterval(() => {
        retryCount++;
        const currentHash = evmWallet?.hash;

        if (currentHash && currentHash !== initialWalletHash.current) {
          clearInterval(interval!);

          // Check transaction status before updating the UI
          (async () => {
            // Check if the transaction was successful
            const { success, errorMessage } = await checkTransactionStatus(
              ensureValidHash(currentHash),
              evmData.chainId,
            );
            const txStatus = success ? 'success' : 'fail';

            // Update transaction with hash and status
            setTransaction((prev: TransactionState | null) =>
              prev
                ? {
                    ...prev,
                    txHash: currentHash,
                    status: success
                      ? 'success'
                      : ('error' as TransactionStatus),
                    error: errorMessage,
                  }
                : null,
            );

            setIsLoading(false);

            // Append message if not already appended
            if (!messageAppended.current) {
              messageAppended.current = true;
              await append(
                createEvmTransactionStatusMessage(
                  evmData,
                  txStatus,
                  currentHash,
                  messageId,
                  errorMessage,
                ),
              );
            }
          })();
        }

        // Give up after maximum retries
        if (retryCount >= MAX_RETRIES) {
          clearInterval(interval!);

          if (isLoading) {
            setIsLoading(false);

            // If we still don't have a hash but the transaction was started,
            // set a fallback UI state
            if (!messageAppended.current) {
              messageAppended.current = true;
              (async () => {
                await append(
                  createEvmTransactionStatusMessage(
                    evmData,
                    'pending',
                    undefined,
                    messageId,
                    'Transaction submitted. Check your wallet history for confirmation.',
                  ),
                );
              })();
            }
          }
        }
      }, 500);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    isLoading,
    transaction,
    evmWallet,
    evmData,
    messageId,
    append,
    setTransaction,
  ]);

  return {
    isLoading,
    setIsLoading,
    setErrorMsg,
    errorMsg,
    transaction,
    handleSmartAction,
    handleTxn,
    checkTransactionStatus,
    getRpcUrlForChain,
    network,
    evmData,
    evmAddress,
    setTransaction,
  };
};
